import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, mediaUrl } from "@/lib/api";
import { AField, BiField, Btn, PageHead } from "@/admin/AdminLayout";

const SECTION_LABELS = { positioning: "Positioning statement", featured: "Featured vehicles", sold: "Recently sold strip (shows only when sold vehicles are published)", cinematic: "Cinematic visual / video", sourcing: "Sourcing CTA", network: "International network", sell: "Sell teaser", about: "About", contact: "Contact" };
const VISUALS = [["cinematic", "Cinematic section (replaces old Koenigsegg video)"], ["sourcing", "Sourcing section"], ["sell", "Sell section"], ["about", "About section"], ["contact", "Contact page header"]];

const MediaSelect = ({ value, onChange, media, kind, testId }) => (
  <div className="flex gap-2">
    <select className="field" value={media.some((m) => m.url === value) ? value : "__custom"} onChange={(e) => e.target.value !== "__custom" && onChange(e.target.value)} data-testid={testId}>
      <option value="">— none —</option>
      <option value="__custom">Custom URL ↓</option>
      {media.filter((m) => m.kind === kind).map((m) => <option key={m.id} value={m.url}>{m.original_filename || m.url}</option>)}
    </select>
  </div>
);

export default function HomepageContent() {
  const qc = useQueryClient();
  const [s, setS] = useState(null);
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const { data: media = [] } = useQuery({ queryKey: ["admin-media", ""], queryFn: async () => (await api.get("/admin/media")).data });
  const { data: vehicles = [] } = useQuery({ queryKey: ["admin-vehicles"], queryFn: async () => (await api.get("/admin/vehicles")).data });
  useEffect(() => { if (data) setS(data); }, [data]);
  if (!s) return null;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setVisual = (k, v) => setS((x) => ({ ...x, visuals: { ...x.visuals, [k]: v } }));
  const move = (i, d) => { const o = [...s.section_order]; const j = i + d; if (j < 0 || j >= o.length) return; [o[i], o[j]] = [o[j], o[i]]; set("section_order", o); };
  const toggleFeatured = (id) => set("featured_vehicle_ids", s.featured_vehicle_ids.includes(id) ? s.featured_vehicle_ids.filter((x) => x !== id) : [...s.featured_vehicle_ids, id]);
  const save = async () => {
    try {
      await api.put("/admin/settings", s);
      toast.success("Homepage updated");
      qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["featured"] }); qc.invalidateQueries({ queryKey: ["admin-settings"] });
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const videoField = (k, label, testId) => (
    <AField label={label}>
      <MediaSelect value={s[k]} onChange={(v) => set(k, v)} media={media} kind="video" testId={testId} />
      <input className="field mt-2" value={s[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder="or paste a URL (/media/gt3rs-loop.mp4)" />
    </AField>
  );

  return (
    <div data-testid="admin-homepage">
      <PageHead title="Homepage Content"><Btn onClick={save} data-testid="homepage-save">SAVE</Btn></PageHead>
      <div className="space-y-5">
        <div className="admin-card space-y-5 p-5">
          <p className="mono text-[0.58rem] text-[#8b95a5]">Hero</p>
          <BiField label="Hero title" value={s.hero_title} onChange={(v) => set("hero_title", v)} />
          <BiField label="Hero subtitle" value={s.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} />
          <BiField label="Hero tagline" value={s.hero_tagline} onChange={(v) => set("hero_tagline", v)} />
          <div className="grid gap-4 sm:grid-cols-2">
            {videoField("hero_video_url", "Hero video (desktop)", "hero-video-select")}
            {videoField("hero_video_mobile_url", "Hero video (mobile, optional vertical)", "hero-video-mobile-select")}
            <AField label="Hero poster / fallback image URL"><input className="field" value={s.hero_poster_url || ""} onChange={(e) => set("hero_poster_url", e.target.value)} data-testid="hero-poster-input" /></AField>
            <AField label="Mobile poster image URL"><input className="field" value={s.hero_poster_mobile_url || ""} onChange={(e) => set("hero_poster_mobile_url", e.target.value)} data-testid="hero-poster-mobile-input" /></AField>
            <AField label="Hero image (used when no video)"><input className="field" value={s.hero_image_url || ""} onChange={(e) => set("hero_image_url", e.target.value)} /></AField>
          </div>
          {s.hero_video_url && <video key={s.hero_video_url} src={mediaUrl(s.hero_video_url)} className="aspect-video w-full max-w-md" muted loop autoPlay playsInline />}
          {s.hero_video_mobile_url && <video key={s.hero_video_mobile_url} src={mediaUrl(s.hero_video_mobile_url)} className="aspect-[9/16] w-40" muted loop autoPlay playsInline />}
        </div>
        <div className="admin-card space-y-4 p-5">
          <p className="mono text-[0.58rem] text-[#8b95a5]">Section visuals (each section must use a different image)</p>
          {videoField("section_video_url", "Cinematic section video (optional — replaces the photo)", "section-video-select")}
          <div className="grid gap-4 sm:grid-cols-2">
            {VISUALS.map(([k, label]) => (
              <AField key={k} label={label}>
                <div className="flex gap-3">
                  <div className="h-14 w-20 shrink-0 overflow-hidden bg-[#0e1013]">{s.visuals?.[k] && <img src={mediaUrl(s.visuals[k])} alt="" className="h-full w-full object-cover" />}</div>
                  <div className="flex-1 space-y-2">
                    <MediaSelect value={s.visuals?.[k]} onChange={(v) => setVisual(k, v)} media={media} kind="image" testId={`visual-${k}`} />
                    <input className="field" value={s.visuals?.[k] || ""} onChange={(e) => setVisual(k, e.target.value)} />
                  </div>
                </div>
              </AField>
            ))}
          </div>
        </div>
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="admin-card p-5">
            <p className="mono mb-4 text-[0.58rem] text-[#8b95a5]">Section order</p>
            {s.section_order.map((k, i) => (
              <div key={k} className="flex items-center justify-between border-t border-white/5 py-2 text-sm" data-testid={`section-order-${k}`}>
                <span>{SECTION_LABELS[k] || k}</span>
                <span className="flex gap-1 text-[#8b95a5]"><button onClick={() => move(i, -1)} className="p-1 hover:text-white"><ArrowUp size={14} /></button><button onClick={() => move(i, 1)} className="p-1 hover:text-white"><ArrowDown size={14} /></button></span>
              </div>
            ))}
          </div>
          <div className="admin-card p-5">
            <p className="mono mb-4 text-[0.58rem] text-[#8b95a5]">Featured vehicles (empty = vehicles flagged "featured")</p>
            {vehicles.map((v) => (
              <label key={v.id} className="flex items-center gap-3 border-t border-white/5 py-2 text-sm" data-testid={`featured-toggle-${v.slug}`}>
                <input type="checkbox" checked={s.featured_vehicle_ids.includes(v.id)} onChange={() => toggleFeatured(v.id)} />
                <span>{v.make} {v.model} <span className="text-[#8b95a5]">{v.version}{!v.published && " · draft"}</span></span>
              </label>
            ))}
          </div>
        </div>
        <div className="admin-card space-y-5 p-5">
          <p className="mono text-[0.58rem] text-[#8b95a5]">Calls to action</p>
          <BiField label="Sourcing CTA" value={s.sourcing_cta} onChange={(v) => set("sourcing_cta", v)} />
          <BiField label="Contact CTA" value={s.contact_cta} onChange={(v) => set("contact_cta", v)} />
        </div>
      </div>
    </div>
  );
}
